import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  Badge,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

const EnhancedCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  background: theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
    borderColor: theme.palette.primary.main,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  boxShadow: theme.shadows[2],
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const statusColors = {
    verified: { bg: theme.palette.success.main, color: theme.palette.success.contrastText },
    unverified: { bg: theme.palette.grey[400], color: theme.palette.getContrastText(theme.palette.grey[400]) },
  };
  const colorConfig = statusColors[status] || statusColors.unverified;
  return {
    backgroundColor: colorConfig.bg,
    color: colorConfig.color,
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'capitalize',
    borderRadius: 6,
  };
});

const WALLET_TYPES = {
  paypal: { label: 'PayPal', icon: '💙' },
  stripe: { label: 'Stripe Connect', icon: '🟣' },
  wise: { label: 'Wise', icon: '🟢' },
  bank_ach: { label: 'Bank/ACH', icon: '🏦' },
  crypto: { label: 'Crypto', icon: '₿' },
};

const WalletCard = ({ wallet, onSetPrimary }) => {
  const getWalletDetails = (w) => {
    switch (w.wallet_type) {
      case 'paypal':
        return w.paypal_email;
      case 'wise':
        return `${w.wise_email}${w.wise_currency ? ` (${w.wise_currency})` : ''}`;
      case 'bank_ach':
        return `${w.bank_name || 'Bank'}${w.bank_account_number ? ` ${w.bank_account_number}` : ''}`;
      case 'crypto':
        return `${w.crypto_currency || ''} ${w.crypto_wallet_address ? `${String(w.crypto_wallet_address).slice(0, 8)}…` : ''}`;
      default:
        return w.nickname || 'Payment method';
    }
  };

  const walletType = WALLET_TYPES[wallet.wallet_type];

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      mb: 1,
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: 'action.hover',
        borderColor: 'primary.main',
      }
    }}>
      <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
        {walletType?.icon || '💳'}
      </Typography>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" fontWeight={600}>
            {wallet.nickname || walletType?.label || 'Payment Method'}
          </Typography>
          {wallet.is_primary && <StarIcon sx={{ color: 'primary.main', fontSize: 16 }} />}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {getWalletDetails(wallet)}
        </Typography>
      </Box>
      <StatusChip 
        size="small" 
        label={wallet.verification_status || 'unverified'} 
        status={wallet.verification_status || 'unverified'} 
      />
    </Box>
  );
};

const WalletManagementSection = ({
  wallets = [],
  walletsLoading,
  taxInfo,
  hasVerifiedWallets,
  onSetPrimaryWallet,
  onNavigateToSettings,
}) => {
  return (
    <EnhancedCard sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon /> Payment Methods
            {!hasVerifiedWallets && (
              <Badge badgeContent="!" color="warning" sx={{ ml: 1 }}>
                <Box />
              </Badge>
            )}
          </Typography>
          <Stack direction="row" spacing={1}>
            <ActionButton 
              variant="outlined" 
              size="small" 
              startIcon={<SettingsIcon />} 
              onClick={() => onNavigateToSettings('wallets')}
            >
              Manage
            </ActionButton>
            <ActionButton 
              variant="contained" 
              size="small" 
              startIcon={<AddIcon />} 
              onClick={() => onNavigateToSettings('wallets')}
            >
              Add Method
            </ActionButton>
          </Stack>
        </Box>

        {walletsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : wallets.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body1" gutterBottom>No payment methods configured</Typography>
            <Typography variant="body2">
              Add a payment method to receive payouts directly. Click "Add Method" to get started.
            </Typography>
          </Alert>
        ) : (
          <Stack spacing={1}>
            {wallets.slice(0, 3).map((wallet) => (
              <WalletCard 
                key={wallet.id} 
                wallet={wallet} 
                onSetPrimary={onSetPrimaryWallet}
              />
            ))}
            {wallets.length > 3 && (
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Button 
                  variant="text" 
                  size="small" 
                  onClick={() => onNavigateToSettings('wallets')}
                >
                  View all {wallets.length} payment methods
                </Button>
              </Box>
            )}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="body1" fontWeight={600}>Tax Information</Typography>
            <Typography variant="body2" color="text.secondary">
              {taxInfo ? 'Completed' : 'Required for payments over $600'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatusChip 
              size="small" 
              label={taxInfo ? 'Complete' : 'Incomplete'} 
              status={taxInfo ? 'verified' : 'unverified'} 
            />
            <ActionButton 
              variant="text" 
              size="small" 
              onClick={() => onNavigateToSettings('wallets')}
            >
              {taxInfo ? 'View' : 'Setup'}
            </ActionButton>
          </Box>
        </Box>
      </CardContent>
    </EnhancedCard>
  );
};

export default WalletManagementSection;